import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@/components/ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { 
  Package, 
  Truck, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  History,
  Search,
  PackageCheck,
  RefreshCw,
  ChevronDown,
  ChevronRight
} from 'lucide-react';

const WarehouseReceptions = () => {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [receptionHistory, setReceptionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [orderHistory, setOrderHistory] = useState([]);
  const [receiveFormData, setReceiveFormData] = useState({
    quantity: 0,
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [expandedSuppliers, setExpandedSuppliers] = useState({});
  const [expandedPOs, setExpandedPOs] = useState({});
  const [statusFilter, setStatusFilter] = useState('active'); // 'active' hides fully received

  useEffect(() => {
    fetchPendingOrders();
    fetchReceptionHistory();
  }, []);

  // Initialize expanded state when orders load - all collapsed by default
  useEffect(() => {
    if (pendingOrders.length > 0) {
      setExpandedSuppliers({});
      setExpandedPOs({});
    }
  }, [pendingOrders]);

  const fetchPendingOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/receptions/pending-orders`);
      setPendingOrders(response.data.orders || []);
    } catch (error) {
      toast.error('Falha ao carregar encomendas pendentes');
    } finally {
      setLoading(false);
    }
  };

  const fetchReceptionHistory = async () => {
    try {
      setHistoryLoading(true);
      const params = new URLSearchParams({ page_size: '100' });
      if (sourceFilter !== 'all') params.append('source', sourceFilter);
      
      const response = await axios.get(`${API}/receptions?${params.toString()}`);
      setReceptionHistory(response.data.items || []);
    } catch (error) {
      console.error('Falha ao carregar histórico');
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchOrderHistory = async (orderId) => {
    try {
      const response = await axios.get(`${API}/receptions/history/${orderId}`);
      setOrderHistory(response.data.receptions || []);
    } catch (error) {
      toast.error('Falha ao carregar histórico da encomenda');
    }
  };

  // Group orders by supplier, then by PO number
  const groupedOrders = useMemo(() => {
    // Filter by search term
    let filteredOrders = pendingOrders.filter(order => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        order.po_number?.toLowerCase().includes(term) ||
        order.sku?.toLowerCase().includes(term) ||
        order.product_name?.toLowerCase().includes(term) ||
        order.supplier?.toLowerCase().includes(term)
      );
    });

    // Filter by status - by default show only active (with items to receive)
    if (statusFilter === 'active') {
      filteredOrders = filteredOrders.filter(o => o.status !== 'received');
    } else if (statusFilter === 'partial') {
      filteredOrders = filteredOrders.filter(o => o.status === 'partially_received');
    } else if (statusFilter === 'pending') {
      filteredOrders = filteredOrders.filter(o => o.status === 'pending');
    } else if (statusFilter === 'ordered') {
      filteredOrders = filteredOrders.filter(o => ['approved', 'ordered'].includes(o.status));
    }
    // 'all' shows everything

    // Group by supplier
    const bySupplier = {};
    filteredOrders.forEach(order => {
      const supplier = order.supplier || 'Sem Fornecedor';
      if (!bySupplier[supplier]) {
        bySupplier[supplier] = {
          name: supplier,
          orders: {},
          totalOrders: 0,
          totalItems: 0,
          totalQuantity: 0,
          receivedQuantity: 0,
          remainingQuantity: 0
        };
      }
      
      const poNumber = order.po_number;
      if (!bySupplier[supplier].orders[poNumber]) {
        bySupplier[supplier].orders[poNumber] = {
          po_number: poNumber,
          items: [],
          totalQuantity: 0,
          receivedQuantity: 0,
          remainingQuantity: 0,
          status: 'received',
          created_at: order.created_at
        };
        bySupplier[supplier].totalOrders++;
      }
      
      const receivedQty = order.received_quantity || 0;
      const remainingQty = order.quantity - receivedQty;
      
      bySupplier[supplier].orders[poNumber].items.push(order);
      bySupplier[supplier].orders[poNumber].totalQuantity += order.quantity || 0;
      bySupplier[supplier].orders[poNumber].receivedQuantity += receivedQty;
      bySupplier[supplier].orders[poNumber].remainingQuantity += remainingQty;
      
      // Update PO status
      const itemStatus = order.status;
      const currentPOStatus = bySupplier[supplier].orders[poNumber].status;
      if (itemStatus === 'pending' || itemStatus === 'approved' || itemStatus === 'ordered') {
        if (currentPOStatus === 'received' || currentPOStatus === 'partially_received') {
          bySupplier[supplier].orders[poNumber].status = 'partially_received';
        } else {
          bySupplier[supplier].orders[poNumber].status = itemStatus;
        }
      } else if (itemStatus === 'partially_received' && currentPOStatus === 'received') {
        bySupplier[supplier].orders[poNumber].status = 'partially_received';
      }
      
      // Update supplier totals
      bySupplier[supplier].totalItems++;
      bySupplier[supplier].totalQuantity += order.quantity || 0;
      bySupplier[supplier].receivedQuantity += receivedQty;
      bySupplier[supplier].remainingQuantity += remainingQty;
    });

    // Convert to array and sort by remaining quantity (highest first)
    return Object.values(bySupplier)
      .map(supplier => ({
        ...supplier,
        orders: Object.values(supplier.orders).sort((a, b) => b.remainingQuantity - a.remainingQuantity)
      }))
      .sort((a, b) => b.remainingQuantity - a.remainingQuantity);
  }, [pendingOrders, searchTerm, statusFilter]);

  // KPI cards count ENCOMENDAS (distinct PO numbers), not article lines.
  const poSummary = useMemo(() => {
    const byPo = {};
    pendingOrders.forEach((o) => {
      (byPo[o.po_number] = byPo[o.po_number] || new Set()).add(o.status);
    });
    const counts = { active: 0, pending: 0, partial: 0, ordered: 0, total: 0 };
    Object.values(byPo).forEach((statuses) => {
      counts.total++;
      const hasOpen = ['pending', 'approved', 'ordered'].some((s) => statuses.has(s));
      let st;
      if (statuses.has('partially_received') || (hasOpen && statuses.has('received'))) st = 'partial';
      else if (statuses.has('pending')) st = 'pending';
      else if (statuses.has('approved') || statuses.has('ordered')) st = 'ordered';
      else st = 'received';
      if (st !== 'received') counts.active++;
      if (st === 'pending') counts.pending++;
      if (st === 'partial') counts.partial++;
      if (st === 'ordered') counts.ordered++;
    });
    return counts;
  }, [pendingOrders]);

  const handleOpenReceive = (order) => {
    setSelectedOrder(order);
    setReceiveFormData({
      quantity: order.quantity - (order.received_quantity || 0),
      notes: ''
    });
    setShowReceiveModal(true);
  };

  const handleOpenHistory = async (order) => {
    setSelectedOrder(order);
    await fetchOrderHistory(order.id);
    setShowHistoryModal(true);
  };

  const handleReceive = async () => {
    if (!selectedOrder) return;
    
    if (receiveFormData.quantity <= 0) {
      toast.error('Quantidade deve ser maior que zero');
      return;
    }

    const remaining = selectedOrder.quantity - (selectedOrder.received_quantity || 0);
    if (receiveFormData.quantity > remaining) {
      toast.error(`Quantidade não pode exceder ${remaining} (restante)`);
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API}/purchase-orders/${selectedOrder.id}/receive`, {
        quantity_received: receiveFormData.quantity,
        notes: receiveFormData.notes || null
      });
      
      toast.success(`Receção registada: ${receiveFormData.quantity} unidades`);
      setShowReceiveModal(false);
      setSelectedOrder(null);
      fetchPendingOrders();
      fetchReceptionHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Falha ao registar receção');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleSupplier = (supplier) => {
    setExpandedSuppliers(prev => ({ ...prev, [supplier]: !prev[supplier] }));
  };

  const togglePO = (poNumber) => {
    setExpandedPOs(prev => ({ ...prev, [poNumber]: !prev[poNumber] }));
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'received':
        return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200"><CheckCircle className="h-3 w-3 mr-1" />Recebido</Badge>;
      case 'partially_received':
        return <Badge className="bg-blue-100 text-blue-700 border-blue-200"><PackageCheck className="h-3 w-3 mr-1" />Parcial</Badge>;
      case 'ordered':
        return <Badge className="bg-purple-100 text-purple-700 border-purple-200"><Truck className="h-3 w-3 mr-1" />Encomendado</Badge>;
      case 'approved':
        return <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200"><CheckCircle className="h-3 w-3 mr-1" />Aprovado</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-700 border-amber-200"><Clock className="h-3 w-3 mr-1" />Pendente</Badge>;
    }
  };

  const getSourceBadge = (source, sageDocument) => {
    if (source === 'sage_100c') {
      return (
        <div className="flex items-center gap-1">
          <Badge className="bg-green-100 text-green-700">SAGE</Badge>
          {sageDocument && <span className="text-xs font-mono text-muted-foreground">{sageDocument}</span>}
        </div>
      );
    }
    return <Badge className="bg-orange-100 text-orange-700">Manual</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 md:p-8 max-w-[1600px] mx-auto" data-testid="warehouse-receptions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 sm:mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">Receções</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {poSummary.total} {poSummary.total === 1 ? 'encomenda' : 'encomendas'} • {pendingOrders.length} {pendingOrders.length === 1 ? 'artigo' : 'artigos'} • {groupedOrders.length} {groupedOrders.length === 1 ? 'fornecedor' : 'fornecedores'}
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => { fetchPendingOrders(); fetchReceptionHistory(); }}
          data-testid="refresh-button"
          className="w-full sm:w-auto"
          size="sm"
        >
          <RefreshCw className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Atualizar</span>
        </Button>
      </div>

      {/* Summary Cards - Clickable filters */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4 sm:mb-6">
        <Card 
          className={`border rounded-sm shadow-none cursor-pointer transition-all ${statusFilter === 'active' ? 'ring-2 ring-red-400' : 'border-slate-200 hover:border-slate-300'} bg-slate-50/50`}
          onClick={() => setStatusFilter('active')}
        >
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Ativos</p>
                <p className="text-xl sm:text-2xl font-bold font-mono" data-testid="kpi-ativos">{poSummary.active}</p>
              </div>
              <Package className="h-6 w-6 sm:h-8 sm:w-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`border rounded-sm shadow-none cursor-pointer transition-all ${statusFilter === 'pending' ? 'ring-2 ring-amber-400' : 'border-amber-200 hover:border-amber-300'} bg-amber-50/50`}
          onClick={() => setStatusFilter(statusFilter === 'pending' ? 'active' : 'pending')}
        >
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">A Receber</p>
                <p className="text-xl sm:text-2xl font-bold font-mono text-amber-700" data-testid="kpi-a-receber">{poSummary.pending}</p>
              </div>
              <Clock className="h-6 w-6 sm:h-8 sm:w-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`border rounded-sm shadow-none cursor-pointer transition-all ${statusFilter === 'partial' ? 'ring-2 ring-blue-400' : 'border-blue-200 hover:border-blue-300'} bg-blue-50/50`}
          onClick={() => setStatusFilter(statusFilter === 'partial' ? 'active' : 'partial')}
        >
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Parciais</p>
                <p className="text-xl sm:text-2xl font-bold font-mono text-blue-700" data-testid="kpi-parciais">{poSummary.partial}</p>
              </div>
              <PackageCheck className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`border rounded-sm shadow-none cursor-pointer transition-all ${statusFilter === 'ordered' ? 'ring-2 ring-purple-400' : 'border-purple-200 hover:border-purple-300'} bg-purple-50/50`}
          onClick={() => setStatusFilter(statusFilter === 'ordered' ? 'active' : 'ordered')}
        >
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Encomendadas</p>
                <p className="text-xl sm:text-2xl font-bold font-mono text-purple-700" data-testid="kpi-encomendadas">{poSummary.ordered}</p>
              </div>
              <Truck className="h-6 w-6 sm:h-8 sm:w-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`border rounded-sm shadow-none cursor-pointer transition-all ${statusFilter === 'all' ? 'ring-2 ring-emerald-400' : 'border-emerald-200 hover:border-emerald-300'} bg-emerald-50/50`}
          onClick={() => setStatusFilter(statusFilter === 'all' ? 'active' : 'all')}
        >
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Total</p>
                <p className="text-xl sm:text-2xl font-bold font-mono text-emerald-700" data-testid="kpi-total">{poSummary.total}</p>
              </div>
              <CheckCircle className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter indicator */}
      {statusFilter !== 'active' && (
        <div className="mb-4 flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Filtro:</span>
          <Badge variant="outline" className="cursor-pointer" onClick={() => setStatusFilter('active')}>
            {statusFilter === 'pending' ? 'A Receber' : statusFilter === 'partial' ? 'Parciais' : statusFilter === 'ordered' ? 'Encomendadas' : 'Todas'}
            <span className="ml-1">×</span>
          </Badge>
        </div>
      )}

      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList className="bg-slate-100 w-full sm:w-auto grid grid-cols-2 sm:flex">
          <TabsTrigger value="pending" data-testid="tab-pending" className="text-xs sm:text-sm">
            <Package className="h-4 w-4 mr-1.5 sm:mr-2" />
            Por Receber
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history" className="text-xs sm:text-sm">
            <History className="h-4 w-4 mr-1.5 sm:mr-2" />
            Histórico
          </TabsTrigger>
        </TabsList>

        {/* Pending Orders Tab */}
        <TabsContent value="pending">
          {/* Search */}
          <div className="mb-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="search-orders"
                placeholder="Pesquisar por nº encomenda, SKU, produto..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Grouped Orders */}
          {groupedOrders.length === 0 ? (
            <Card className="border border-slate-200 rounded-sm shadow-none">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <CheckCircle className="h-16 w-16 text-emerald-300 mb-4" />
                <p className="text-lg font-medium text-slate-900 mb-2">
                  {statusFilter === 'active' ? 'Sem receções pendentes' : 'Nenhuma receção com este filtro'}
                </p>
                <p className="text-sm text-muted-foreground mb-4">
                  {statusFilter === 'active' ? 'Todas as encomendas foram recebidas' : ''}
                </p>
                <div className="flex gap-2">
                  {statusFilter !== 'active' && (
                    <Button variant="outline" onClick={() => setStatusFilter('active')}>Ver Ativas</Button>
                  )}
                  <Button variant="outline" onClick={() => setStatusFilter('all')}>Ver Todas ({pendingOrders.length})</Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {groupedOrders.map((supplierGroup) => (
                <Card key={supplierGroup.name} className={`border rounded-sm shadow-none ${supplierGroup.remainingQuantity > 0 ? 'border-amber-200' : 'border-slate-200'}`}>
                  {/* Supplier Header */}
                  <Collapsible open={expandedSuppliers[supplierGroup.name]} onOpenChange={() => toggleSupplier(supplierGroup.name)}>
                    <CollapsibleTrigger asChild>
                      <CardHeader className={`cursor-pointer hover:bg-slate-50/50 transition-colors py-3 px-4 ${supplierGroup.remainingQuantity > 0 ? 'bg-amber-50/30' : ''}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {expandedSuppliers[supplierGroup.name] ? (
                              <ChevronDown className="h-5 w-5 text-slate-400" />
                            ) : (
                              <ChevronRight className="h-5 w-5 text-slate-400" />
                            )}
                            <Truck className="h-5 w-5 text-red-600" />
                            <div>
                              <CardTitle className="text-base sm:text-lg font-semibold">{supplierGroup.name}</CardTitle>
                              <p className="text-xs sm:text-sm text-muted-foreground">
                                {supplierGroup.totalOrders} encomenda{supplierGroup.totalOrders !== 1 ? 's' : ''} • {supplierGroup.totalItems} artigo{supplierGroup.totalItems !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">Por receber</p>
                            <p className={`font-mono font-bold text-lg sm:text-xl ${supplierGroup.remainingQuantity > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                              {supplierGroup.remainingQuantity} <span className="text-sm font-normal">un.</span>
                            </p>
                          </div>
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <CardContent className="pt-0 px-4 pb-4">
                        <div className="space-y-3">
                          {supplierGroup.orders.map((po) => (
                            <Collapsible key={po.po_number} open={expandedPOs[po.po_number]} onOpenChange={() => togglePO(po.po_number)}>
                              {/* PO Header */}
                              <div className={`rounded-sm border ${po.remainingQuantity > 0 ? 'border-amber-200 bg-amber-50/20' : 'border-slate-200 bg-slate-50/30'}`}>
                                <CollapsibleTrigger asChild>
                                  <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50/50 transition-colors">
                                    <div className="flex items-center gap-2 sm:gap-3">
                                      {expandedPOs[po.po_number] ? (
                                        <ChevronDown className="h-4 w-4 text-slate-400" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4 text-slate-400" />
                                      )}
                                      <div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="font-mono text-sm font-semibold">{po.po_number}</span>
                                          {getStatusBadge(po.status)}
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                          {po.items.length} artigo{po.items.length !== 1 ? 's' : ''} • {po.receivedQuantity}/{po.totalQuantity} recebidos
                                        </p>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-xs text-muted-foreground">Por receber</p>
                                      <span className={`font-mono font-bold ${po.remainingQuantity > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                        {po.remainingQuantity} un.
                                      </span>
                                    </div>
                                  </div>
                                </CollapsibleTrigger>

                                {/* PO Items */}
                                <CollapsibleContent>
                                  <div className="border-t border-slate-200">
                                    {po.items.map((item, idx) => {
                                      const receivedQty = item.received_quantity || 0;
                                      const remainingQty = item.quantity - receivedQty;
                                      const progress = Math.round((receivedQty / item.quantity) * 100);
                                      
                                      return (
                                        <div 
                                          key={item.id} 
                                          className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 ${idx !== po.items.length - 1 ? 'border-b border-slate-100' : ''} ${remainingQty > 0 ? 'bg-amber-50/30' : ''}`}
                                        >
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                              <span className="font-mono text-xs text-red-600">{item.sku}</span>
                                              <span className="text-sm truncate">{item.product_name}</span>
                                            </div>
                                            <div className="flex items-center gap-3 mt-1">
                                              {/* Progress bar */}
                                              <div className="w-20">
                                                <div className="w-full bg-slate-200 rounded-full h-1.5">
                                                  <div
                                                    className={`h-1.5 rounded-full ${progress >= 100 ? 'bg-emerald-500' : progress > 0 ? 'bg-blue-500' : 'bg-slate-300'}`}
                                                    style={{ width: `${Math.min(progress, 100)}%` }}
                                                  />
                                                </div>
                                              </div>
                                              <span className="text-xs text-muted-foreground font-mono">
                                                {receivedQty}/{item.quantity}
                                              </span>
                                              {remainingQty > 0 && (
                                                <span className="text-xs text-amber-600 font-mono font-semibold">
                                                  Falta: {remainingQty}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-2 flex-shrink-0">
                                            {remainingQty > 0 ? (
                                              <Button
                                                size="sm"
                                                className="bg-emerald-600 hover:bg-emerald-700 h-10 sm:h-8 px-4 sm:px-3"
                                                onClick={() => handleOpenReceive(item)}
                                                data-testid={`receive-${item.id}`}
                                              >
                                                <PackageCheck className="h-3.5 w-3.5 mr-1.5" />
                                                Receber
                                              </Button>
                                            ) : (
                                              <Badge className="bg-emerald-100 text-emerald-700">
                                                <CheckCircle className="h-3 w-3 mr-1" />
                                                Completo
                                              </Badge>
                                            )}
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-10 w-10 sm:h-8 sm:w-8 p-0"
                                              onClick={() => handleOpenHistory(item)}
                                              data-testid={`history-${item.id}`}
                                            >
                                              <History className="h-4 w-4" />
                                            </Button>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </CollapsibleContent>
                              </div>
                            </Collapsible>
                          ))}
                        </div>
                      </CardContent>
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <div className="mb-4 flex gap-4">
            <Select value={sourceFilter} onValueChange={(v) => { setSourceFilter(v); setTimeout(fetchReceptionHistory, 100); }}>
              <SelectTrigger className="w-[180px]" data-testid="source-filter">
                <SelectValue placeholder="Filtrar por origem" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Todas as Origens</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="sage_100c">SAGE 100C</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {historyLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
            </div>
          ) : receptionHistory.length === 0 ? (
            <Card className="border border-slate-200 rounded-sm shadow-none">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <History className="h-16 w-16 text-slate-300 mb-4" />
                <p className="text-lg font-medium text-slate-900 mb-2">Sem histórico</p>
                <p className="text-sm text-muted-foreground">Ainda não foram registadas receções</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="border border-slate-200 rounded-sm shadow-none">
              <CardContent className="p-0">
                {/* Mobile: stacked cards */}
                <div className="md:hidden divide-y divide-slate-100" data-testid="reception-history-mobile">
                  {receptionHistory.map((reception) => (
                    <div key={reception.id} className="p-3 space-y-1.5">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-slate-900 leading-snug flex-1 min-w-0">{reception.product_name}</p>
                        <span className="font-mono text-emerald-600 font-bold text-sm flex-shrink-0">+{reception.quantity_received}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2 text-[11px] text-slate-500">
                        <span className="font-mono truncate">{reception.po_number} · {reception.supplier || 'N/A'}</span>
                        <span className="font-mono flex-shrink-0">{reception.total_received_after}/{reception.quantity_ordered}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        {getSourceBadge(reception.source, reception.sage_document)}
                        <span className="text-[11px] text-slate-400">{new Date(reception.created_at).toLocaleDateString('pt-PT')}</span>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Desktop: table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-xs lg:text-sm min-w-[850px]">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50/50">
                        <th className="text-left py-2 lg:py-3 px-2 lg:px-3 font-medium text-muted-foreground">Data</th>
                        <th className="text-left py-2 lg:py-3 px-2 lg:px-3 font-medium text-muted-foreground">Nº Enc.</th>
                        <th className="text-left py-2 lg:py-3 px-2 lg:px-3 font-medium text-muted-foreground">Fornecedor</th>
                        <th className="text-left py-2 lg:py-3 px-2 lg:px-3 font-medium text-muted-foreground">Produto</th>
                        <th className="text-left py-2 lg:py-3 px-2 lg:px-3 font-medium text-muted-foreground hidden xl:table-cell">SKU</th>
                        <th className="text-right py-2 lg:py-3 px-2 lg:px-3 font-medium text-muted-foreground">Qtd</th>
                        <th className="text-right py-2 lg:py-3 px-2 lg:px-3 font-medium text-muted-foreground">Total</th>
                        <th className="text-left py-2 lg:py-3 px-2 lg:px-3 font-medium text-muted-foreground">Origem</th>
                        <th className="text-left py-2 lg:py-3 px-2 lg:px-3 font-medium text-muted-foreground hidden xl:table-cell">Utilizador</th>
                      </tr>
                    </thead>
                    <tbody>
                      {receptionHistory.map((reception) => (
                        <tr key={reception.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                          <td className="py-2 lg:py-3 px-2 lg:px-3 text-xs whitespace-nowrap">
                            {new Date(reception.created_at).toLocaleDateString('pt-PT')}
                          </td>
                          <td className="py-2 lg:py-3 px-2 lg:px-3 font-mono text-xs font-medium">{reception.po_number}</td>
                          <td className="py-2 lg:py-3 px-2 lg:px-3 max-w-[100px] truncate" title={reception.supplier}>
                            {reception.supplier || 'N/A'}
                          </td>
                          <td className="py-2 lg:py-3 px-2 lg:px-3 max-w-[150px] truncate">{reception.product_name}</td>
                          <td className="py-2 lg:py-3 px-2 lg:px-3 font-mono text-xs hidden xl:table-cell">{reception.sku}</td>
                          <td className="py-2 lg:py-3 px-2 lg:px-3 font-mono text-right text-emerald-600 font-semibold">
                            +{reception.quantity_received}
                          </td>
                          <td className="py-2 lg:py-3 px-2 lg:px-3 font-mono text-right">
                            {reception.total_received_after}/{reception.quantity_ordered}
                          </td>
                          <td className="py-2 lg:py-3 px-2 lg:px-3">
                            {getSourceBadge(reception.source, reception.sage_document)}
                          </td>
                          <td className="py-2 lg:py-3 px-2 lg:px-3 text-xs hidden xl:table-cell">{reception.received_by_user_name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Receive Modal */}
      <Dialog open={showReceiveModal} onOpenChange={setShowReceiveModal}>
        <DialogContent className="w-[calc(100vw-1.5rem)] sm:max-w-[500px] bg-white max-h-[90vh] overflow-y-auto rounded-xl">
          <DialogHeader>
            <DialogTitle>Registar Receção Manual</DialogTitle>
            <DialogDescription>
              Encomenda {selectedOrder?.po_number}
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4 mt-4">
              <Card className="border border-slate-200 rounded-sm">
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Produto</p>
                      <p className="font-medium">{selectedOrder.product_name}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">SKU</p>
                      <p className="font-mono">{selectedOrder.sku}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Encomendado</p>
                      <p className="font-mono font-semibold">{selectedOrder.quantity}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Já Recebido</p>
                      <p className="font-mono text-emerald-600">{selectedOrder.received_quantity || 0}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-muted-foreground">Restante</p>
                      <p className="font-mono text-lg font-bold text-amber-600">
                        {selectedOrder.quantity - (selectedOrder.received_quantity || 0)} unidades
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="quantity">Quantidade a Receber *</Label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setReceiveFormData((f) => ({ ...f, quantity: Math.max(0, (f.quantity || 0) - 1) }))}
                      disabled={receiveFormData.quantity <= 0}
                      className="h-11 w-11 rounded-lg border border-slate-200 bg-white text-slate-600 text-lg font-semibold active:bg-slate-100 disabled:opacity-40 flex items-center justify-center flex-shrink-0"
                      data-testid="receive-qty-minus"
                    >
                      −
                    </button>
                    <Input
                      id="quantity"
                      type="number"
                      inputMode="numeric"
                      min="1"
                      max={selectedOrder.quantity - (selectedOrder.received_quantity || 0)}
                      value={receiveFormData.quantity}
                      onChange={(e) => setReceiveFormData({ ...receiveFormData, quantity: parseInt(e.target.value) || 0 })}
                      className="h-11 text-center text-base font-mono font-semibold"
                      data-testid="receive-quantity-input"
                    />
                    <button
                      type="button"
                      onClick={() => setReceiveFormData((f) => ({
                        ...f,
                        quantity: Math.min(selectedOrder.quantity - (selectedOrder.received_quantity || 0), (f.quantity || 0) + 1),
                      }))}
                      disabled={receiveFormData.quantity >= selectedOrder.quantity - (selectedOrder.received_quantity || 0)}
                      className="h-11 w-11 rounded-lg border border-slate-200 bg-white text-slate-600 text-lg font-semibold active:bg-slate-100 disabled:opacity-40 flex items-center justify-center flex-shrink-0"
                      data-testid="receive-qty-plus"
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notas (opcional)</Label>
                  <Textarea
                    id="notes"
                    placeholder="Observações..."
                    value={receiveFormData.notes}
                    onChange={(e) => setReceiveFormData({ ...receiveFormData, notes: e.target.value })}
                    data-testid="receive-notes-input"
                  />
                </div>

                {receiveFormData.quantity < (selectedOrder.quantity - (selectedOrder.received_quantity || 0)) && receiveFormData.quantity > 0 && (
                  <div className="flex items-center gap-2 p-3 rounded-sm border border-blue-200 bg-blue-50/50">
                    <AlertCircle className="h-5 w-5 text-blue-500" />
                    <div>
                      <p className="text-sm font-medium text-blue-900">Receção Parcial</p>
                      <p className="text-xs text-blue-700">A encomenda ficará "Parcialmente Recebido"</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="mt-4 flex-col-reverse sm:flex-row gap-2">
            <Button variant="outline" className="w-full sm:w-auto h-11" onClick={() => setShowReceiveModal(false)}>Cancelar</Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto h-11"
              onClick={handleReceive}
              disabled={submitting || receiveFormData.quantity <= 0}
              data-testid="confirm-receive-button"
            >
              {submitting ? 'A processar...' : 'Confirmar Receção'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="w-[calc(100vw-1.5rem)] sm:max-w-[600px] bg-white max-h-[90vh] overflow-y-auto rounded-xl">
          <DialogHeader>
            <DialogTitle>Histórico de Receções</DialogTitle>
            <DialogDescription>
              Encomenda {selectedOrder?.po_number}
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="mt-4">
              <Card className="border border-slate-200 rounded-sm mb-4">
                <CardContent className="p-4">
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Encomendado</p>
                      <p className="font-mono font-semibold">{selectedOrder.quantity}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Recebido</p>
                      <p className="font-mono text-emerald-600 font-semibold">{selectedOrder.received_quantity || 0}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Restante</p>
                      <p className="font-mono text-amber-600 font-semibold">
                        {selectedOrder.quantity - (selectedOrder.received_quantity || 0)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {orderHistory.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Sem receções registadas</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {orderHistory.map((reception) => (
                    <Card key={reception.id} className="border border-slate-200 rounded-sm">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              {getSourceBadge(reception.source, reception.sage_document)}
                              <span className="text-xs text-muted-foreground">
                                {new Date(reception.created_at).toLocaleString('pt-PT')}
                              </span>
                            </div>
                            <p className="text-sm">
                              <span className="font-semibold text-emerald-600">+{reception.quantity_received}</span>
                              <span className="text-muted-foreground"> unidades</span>
                            </p>
                            {reception.notes && (
                              <p className="text-xs text-muted-foreground mt-1">"{reception.notes}"</p>
                            )}
                          </div>
                          <div className="text-right text-sm">
                            <p className="text-muted-foreground">Total após</p>
                            <p className="font-mono font-semibold">{reception.total_received_after}/{reception.quantity_ordered}</p>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">Por: {reception.received_by_user_name}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowHistoryModal(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WarehouseReceptions;
